/* eslint-disable react/static-property-placement */

import React, { Component, MouseEvent, ReactNode, TouchEvent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { v4 as uuid } from "uuid";

import { defined } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { throttle } from "@formant/core/common/throttle";
import {
    AddableModuleType,
    isCustomModuleType,
} from "@formant/core/model/AddableModuleType";
import { duration } from "@formant/core/model/duration";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IComment } from "@formant/core/model/IComment";
import { IDevice } from "@formant/core/model/IDevice";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { IShare } from "@formant/core/model/IShare";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { LayoutName } from "@formant/core/model/LayoutType";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import { IconName } from "@formant/ui/component/common/Icon";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { Measure } from "@formant/ui/component/common/Measure";

import { track } from "../../analytics";
import { ViewContext } from "../../context/TeleopContext/ViewContext";
import { authStore, observeStore, streamStore, viewStore } from "../../stores";
import { breakpoints } from "../../utils/breakpoint";
import { Escape } from "../common/Escape";
import { EventChip } from "../common/EventChip";
import { IconButton } from "../common/IconButton";
import { moduleHasClearBackground } from "../ViewDevice/ViewDevice.helpers";
import styles from "./Module.scss";
import { ModuleConfiguration } from "./ModuleConfiguration";
import { ModuleMenu } from "./ModuleMenu";
import { ModuleMessage } from "./ModuleMessage";
import { ModuleSeek } from "./ModuleSeek";
import { ModuleStreamIcon } from "./ModuleStreamIcon";
import { TooMuchData } from "./TooMuchData";

export interface IModuleProps {
    streamName: string;
    displayName?: string;
    className?: string;
    type?: AddableModuleType;
    icon?: ReactNode;
    device?: IDevice;
    onDemand?: boolean;
    filter: IFilter;
    loading?: boolean;
    empty?: boolean;
    fullscreen?: boolean;
    focused?: boolean;
    editingLayout?: boolean;
    tooMuchData?: boolean;
    noGrid?: boolean;
    teleop?: boolean;
    hideControls?: boolean;
    activeEvent?: IEvent;
    configuration: IViewConfiguration[];
    onFocus: () => void;
    onBlur: () => void;
    onToggleFullscreen: () => void;
    onClose?: () => void;
    onSelectTime?: (time: Date) => void;
    onSeek?: (time: Date) => void;
    onComment?: (comment: Partial<IComment>) => void;
    onAnnotate?: (annotation: Partial<IAnnotation>) => void;
    onDemandData?: (streamName: string) => void;
    onChangeSize?: (size: ModuleSize) => void;
    onConfigureModule?: (moduleName: string) => void;
    onRemoveModule?: (moduleName: string) => void;
    hideDevice: boolean;
    share?: IShare;
    customMenuItems?: { label: string; icon?: IconName; onClick: () => void }[];
    onResetTimeSpan?: () => void;
    children?: React.ReactNode;
    nonInteractable?: boolean;
    moduleMode: ModuleMode;
    preventContentDrag?: boolean;
}

export type ModuleSize = "xl" | "lg" | "md" | "sm" | "xs" | "xxs" | "xxxs";

@observer
export class Module extends Component<IModuleProps> {
    static contextType = ViewContext;
    declare context: React.ContextType<typeof ViewContext>;
    private streamName: string = "";
    private size?: ModuleSize;

    @observable
    private loading?: boolean = false;
    private updateLoading = throttle(
        () => (this.loading = this.props.loading),
        duration.second,
    );

    public componentDidUpdate(prevProps: IModuleProps) {
        if (
            (!this.streamName && this.props.streamName) ||
            prevProps.streamName !== this.props.streamName
        ) {
            this.streamName = this.props.streamName;
        }
    }

    public render() {
        const {
            device,
            streamName,
            className,
            type,
            icon,
            onDemand,
            children,
            empty,
            focused,
            fullscreen,
            editingLayout,
            noGrid,
            teleop,
            tooMuchData,
            filter,
            activeEvent,
            onComment,
            onAnnotate,
            onToggleFullscreen,
            onClose,
            onRemoveModule,
            share,
            displayName,
            customMenuItems,
            onResetTimeSpan,
            nonInteractable,
            moduleMode,
            preventContentDrag,
        } = this.props;

        const { loading, onExitConfiguration } = this;
        const { eventSidebar, configuringModule, configuringFlyout } =
            observeStore;

        this.updateLoading();

        const isAnalytics = streamName.startsWith("analytics-module:");

        const streamAliasOrName =
            displayName ||
            (streamName.startsWith("custom-module:") || isAnalytics
                ? ""
                : streamStore.streamNameAliasOrNameMap?.get(streamName) ||
                  streamName);

        const isConfiguring: boolean = !!(
            moduleMode === ModuleMode.Observe &&
            configuringModule &&
            configuringModule[0] === streamName
        );

        const isConfiguringFlyout: boolean =
            !!(
                moduleMode === ModuleMode.Teleop &&
                configuringFlyout &&
                configuringFlyout[1].streamName === streamName
            ) ||
            observeStore.configuringAnalyticsId ===
                streamName.replace("analytics-module:", "");

        return (
            <Measure
                className={classnames(
                    styles.module,
                    className,
                    !!activeEvent && styles.active,
                    noGrid && styles["no-grid"],
                    activeEvent &&
                        activeEvent.type === "datapoint-event" &&
                        styles[activeEvent.severity],
                    activeEvent &&
                        activeEvent.type === "intervention-request" &&
                        styles["intervention-request"],
                    activeEvent &&
                        activeEvent.type === "comment" &&
                        styles.comment,
                    activeEvent &&
                        activeEvent.type === "custom" &&
                        activeEvent.severity &&
                        styles[activeEvent.severity],
                    editingLayout && styles["editing-layout"],
                    loading && styles.loading,
                    focused && styles.focused,
                    fullscreen && styles.fullscreen,
                    isConfiguring && styles.configuring,
                    isConfiguringFlyout && styles.configuringFlyout,
                    eventSidebar === "open" && styles.sidebar,
                )}
                onMouseDown={this.onMouseDown}
                onTouchStart={this.onTouchStart}
                onClick={this.onClick}
                onMeasureResize={this.onResize}
                data-testid={`stream-module-${streamAliasOrName}`}
                innerRef={this.measureContainerRef}
            >
                {isConfiguring && (
                    <div className={styles["configuration-background"]} />
                )}
                {fullscreen && (
                    <Escape
                        onEsc={() => {
                            if (!observeStore.temporarilyConfiguringModule) {
                                onToggleFullscreen();
                            }
                        }}
                    />
                )}
                {isConfiguring && <Escape onEsc={onExitConfiguration} />}
                <div
                    className={classnames(
                        styles.header,
                        moduleHasClearBackground(
                            this.context.layout,
                            streamName,
                        ) && styles["clear-header"],
                    )}
                    onDoubleClick={
                        !isConfiguring && !isAnalytics
                            ? onToggleFullscreen
                            : undefined
                    }
                    onClick={e => {
                        if (moduleMode === ModuleMode.Teleop) {
                            this.onConfigure();
                        }
                        return this.onClickHeader(e);
                    }}
                    data-testid="module-header"
                >
                    <div
                        className={classnames(styles.dragHandle, "dragHandle")}
                    >
                        <div
                            className={styles.stream}
                            data-tooltip={
                                streamName.startsWith("custom-module:") ||
                                isAnalytics
                                    ? undefined
                                    : displayName || streamName
                            }
                        >
                            <ModuleStreamIcon type={type} icon={icon} />
                            {onDemand &&
                            authStore.featureEnabled("onDemand") ? (
                                <div className={styles["on-demand-marker"]} />
                            ) : null}
                            {streamAliasOrName}
                        </div>
                    </div>
                    {!isConfiguring && (
                        <>
                            {moduleMode === ModuleMode.Teleop &&
                                onRemoveModule && (
                                    <IconButton
                                        key="delete"
                                        icon="delete"
                                        onClicked={this.onRemoveModule}
                                        data-testid="add-view-button"
                                    />
                                )}
                            {moduleMode !== ModuleMode.Teleop && (
                                <ModuleMenu
                                    device={device}
                                    editingLayout={editingLayout}
                                    collapsed={!focused}
                                    onDemandData={
                                        onDemand ? this.onDemandData : undefined
                                    }
                                    layout={this.context.layout}
                                    onComment={onComment && this.onComment}
                                    onAnnotate={onAnnotate && this.onAnnotate}
                                    onFullscreen={onToggleFullscreen}
                                    onClose={onClose}
                                    onClone={this.onClone}
                                    onConfigure={
                                        this.props.onConfigureModule
                                            ? this.onConfigure
                                            : undefined
                                    }
                                    onRemove={
                                        onRemoveModule && this.onRemoveModule
                                    }
                                    filter={filter}
                                    streamName={streamName}
                                    fullscreen={fullscreen}
                                    share={share}
                                    teleop={teleop}
                                    type={type}
                                    customMenuItems={customMenuItems}
                                    onChanged={this.onChanged}
                                />
                            )}
                            {fullscreen && !teleop && (
                                <IconButton
                                    icon="close"
                                    onClicked={onToggleFullscreen}
                                    data-tooltip="Exit fullscreen"
                                />
                            )}
                        </>
                    )}
                </div>

                <div
                    className={classnames(
                        styles.content,
                        preventContentDrag && "preventDrag",
                        moduleHasClearBackground(
                            this.context.layout,
                            streamName,
                        ) && styles["clear-content"],
                        nonInteractable && styles["non-interactable"],
                    )}
                >
                    {isConfiguring && type ? (
                        <ModuleConfiguration
                            device={device}
                            streamName={streamName}
                            filter={filter}
                            moduleType={type}
                        />
                    ) : (
                        <>
                            {children}
                            <ModuleMessage
                                visible={
                                    !!empty &&
                                    !loading &&
                                    !noGrid &&
                                    !tooMuchData &&
                                    !!this.props.onSeek
                                }
                            >
                                <ModuleSeek
                                    filter={filter}
                                    streamName={streamName}
                                    onSeek={this.onSeek}
                                />
                            </ModuleMessage>
                            <LoadingIndicator
                                visible={!!loading}
                                className={styles.loading}
                            />
                        </>
                    )}
                </div>

                {activeEvent && <EventChip event={activeEvent} />}
                <ModuleMessage visible={!!tooMuchData}>
                    <TooMuchData onResetTimeSpan={onResetTimeSpan} />
                </ModuleMessage>
            </Measure>
        );
    }

    private onConfigure = () => {
        const { onConfigureModule, moduleMode } = this.props;

        if (onConfigureModule) {
            if (moduleMode === ModuleMode.Observe) {
                onConfigureModule(this.streamName);
            } else if (this.props.type) {
                observeStore.configuringFlyout = undefined;
                setTimeout(() => {
                    if (this.props.type) {
                        track(
                            `${
                                LayoutName[this.props.moduleMode]
                            }: Customize: Inspecting`,
                            {
                                module: this.streamName,
                            },
                        );
                        observeStore.configuringFlyout = [
                            this.context.layout,
                            {
                                streamName: this.streamName,
                                moduleType: this.props.type,
                                filter: this.props.filter,
                                device: this.props.device,
                            },
                            this.context.onChangeLayout,
                        ];
                    }
                }); // we have to do this since observeStore is eventually consistent when changing between module configurations
            }
        }
    };

    private onExitConfiguration() {
        observeStore.configuringModule = undefined;
    }

    private onMouseDown = (event: MouseEvent<HTMLElement>) => {
        if (this.props.fullscreen) {
            // Prevent module drag behaviors
            event.stopPropagation();
        }
    };

    private onTouchStart = (event: TouchEvent<HTMLElement>) => {
        if (this.props.fullscreen) {
            // Prevent module drag behaviors
            event.stopPropagation();
        }
    };

    private onClick = (event: MouseEvent) => {
        const { focused, onFocus } = this.props;
        event.stopPropagation();
        if (!focused) {
            onFocus();
        }
    };

    onChanged = () => {
        const { focused, onFocus } = this.props;
        if (!focused) {
            onFocus();
        }
    };

    private onClickHeader = (event: MouseEvent) => {
        const { focused, onBlur, fullscreen } = this.props;
        if (focused && !fullscreen) {
            event.stopPropagation();
            onBlur();
        }
    };

    private onSeek = (time: Date) => {
        const { onSeek } = this.props;
        if (onSeek) {
            onSeek(time);
        }
    };

    private onComment = (message?: string) => {
        const { onComment, device } = this.props;
        if (!onComment) {
            return;
        }
        const { streamName, type: streamType } = this.props;
        const comment = {
            streamName,
            deviceId: device?.id,
            streamType: isCustomModuleType(streamType) ? streamType : undefined,
            message,
        };
        onComment(comment as Partial<IComment>);
    };

    private onAnnotate = () => {
        const { onAnnotate, streamName, type: streamType, device } = this.props;
        const annotation = {
            streamName,
            streamType: isCustomModuleType(streamType) ? streamType : undefined,
            deviceId: device?.id,
        };
        defined(onAnnotate)(annotation as Partial<IAnnotation>);
    };

    private onDemandData = () => {
        const { streamName, onDemandData } = this.props;
        if (onDemandData) {
            onDemandData(streamName);
        }
    };

    private onClone = () => {
        const { streamName } = this.props;

        const { viewId, layout, view } = this.context;

        if (streamName && layout?.[streamName] && viewId) {
            const original = layout[streamName];
            layout["analytics-module:" + uuid()] = original;

            fork(viewStore.updateSimple(viewId, { ...view, layout }));
        }
    };

    private onResize = (width: number, _: number) => {
        if (width < breakpoints.xxxs) {
            this.onChangeSize("xxxs");
        } else if (width < breakpoints.xxs) {
            this.onChangeSize("xxs");
        } else if (width < breakpoints.xs) {
            this.onChangeSize("xs");
        } else if (width < breakpoints.sm) {
            this.onChangeSize("sm");
        } else if (width < breakpoints.md) {
            this.onChangeSize("md");
        } else if (width < breakpoints.lg) {
            this.onChangeSize("lg");
        } else {
            this.onChangeSize("xl");
        }
    };

    private onChangeSize = (size: ModuleSize) => {
        if (size !== this.size) {
            this.props.onChangeSize?.(size);
        }
        this.size = size;
    };

    private onRemoveModule = async () => {
        const { onRemoveModule, streamName } = this.props;
        onRemoveModule?.(streamName);
    };

    private measureContainerRef = (element: HTMLDivElement | null) => {
        if (observeStore.autoScrollToModule === this.props.streamName) {
            element?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            observeStore.autoScrollToModule = undefined;
        }
    };
}
